import { useState } from 'react';
import {
  Button,
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  Input,
  Label,
} from '@staff-app/ui';
import { AlertTriangle } from 'lucide-react';
import { intlRequestNo, type IntlRequest } from '@staff-app/shared-types';
import {
  useApproveIntlUnpaidShipment,
  useDeactivateIntlCustomerAccount,
} from '../hooks/useIntlRequests';

/** The shortest reason the server will accept. Mirrored here so the button says why it is off. */
const MIN_REASON = 10;

export type RootOverride = 'shipUnpaid' | 'deactivate';

/**
 * The confirmation for the two ROOT-ONLY overrides.
 *
 * ── WHY A DIALOG AND NOT A BUTTON ──────────────────────────────────────────────────────────────
 *
 * These are the only two acts in this feature that cannot be walked back and that cost somebody
 * money: shipping hardware before the invoice clears, and deactivating a paying customer's
 * account. Every other irreversible thing here at least happens to OUR side of the relationship.
 *
 * THE REASON IS THE POINT, not a formality. Both are judgement calls, and in six months the
 * question is never whether it happened but why it was right — so the field is mandatory, the
 * server refuses anything under ten characters, and the text lands on the row where the next
 * person reads it rather than in an audit table nobody opens.
 *
 * ONE COMPONENT FOR BOTH, because the shape is identical (name the consequence, take a reason,
 * confirm) and two near-identical dialogs would drift apart in exactly the copy that matters.
 * What differs is the WORDING, which is spelled out per action below rather than parameterised
 * into something bland: "this ships before we are paid" and "this turns a customer's screens
 * off" deserve their own sentences.
 */
export function IntlRootOverrideDialog({
  request,
  action,
  onClose,
}: {
  request: IntlRequest;
  action: RootOverride;
  onClose: () => void;
}) {
  const [reason, setReason] = useState('');
  const [approve, { loading: approving, error: approveError }] = useApproveIntlUnpaidShipment();
  const [deactivate, { loading: deactivating, error: deactivateError }] =
    useDeactivateIntlCustomerAccount();

  const shipping = action === 'shipUnpaid';
  const loading = shipping ? approving : deactivating;
  const error = shipping ? approveError : deactivateError;
  const ok = reason.trim().length >= MIN_REASON;

  const run = async () => {
    if (!ok) return;
    try {
      const vars = { variables: { requestId: request.id, reason: reason.trim() } };
      await (shipping ? approve(vars) : deactivate(vars));
      onClose();
    } catch {
      // Rendered below from the hook's `error`. The typed reason stays put — a refusal is
      // information, and retyping a justification is how people shorten it to "asap".
    }
  };

  return (
    <Dialog open onOpenChange={(next) => !next && onClose()}>
      <DialogContent data-testid="intl-root-override-dialog">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <AlertTriangle className="h-4 w-4 text-destructive" />
            {shipping ? 'Ship before payment' : 'Deactivate the customer account'}
          </DialogTitle>
          <DialogDescription>
            {shipping ? (
              <>
                {`${intlRequestNo(request.id)} — ${request.quantity} × `}
                {`${request.deviceModel} to ${request.shipToCountry}. Invoice `}
                {`${request.invoiceNumber ?? request.stripeInvoiceId} is `}
                <span className="font-medium">{request.invoiceStatus ?? 'unpaid'}</span>
                {'. Amazon is told to ship immediately: the order is created AND released in '}
                {'one step, so there is no held state and nothing further to press.'}
              </>
            ) : (
              <>
                {`${request.company ?? request.contactName ?? request.contactEmail} loses access `}
                {'to OptiSigns: their screens stop playing. Re-enabling is a billing decision '}
                {'made when payment arrives, and is not done from this screen.'}
              </>
            )}
          </DialogDescription>
        </DialogHeader>

        {/* States the consequence in its own block rather than only in the description, because
            the description is what people skim. */}
        <div className="rounded-md border border-destructive/40 bg-destructive/5 p-3 text-sm">
          {shipping
            ? 'Hardware leaves the warehouse before the money arrives. If the invoice is never ' +
              'paid, the units are gone.'
            : 'Sets payment.isActive false and forceInActive true on the account — the same ' +
              'action the Devices Order chase uses. It affects the WHOLE account, not just ' +
              'this order.'}
        </div>

        <div>
          <Label htmlFor="intl-override-reason">
            {shipping ? 'Why is this safe to ship?' : 'Why is this justified?'}
          </Label>
          <Input
            id="intl-override-reason"
            className="mt-1"
            value={reason}
            onChange={(e) => setReason(e.target.value)}
            placeholder={
              shipping
                ? 'e.g. Net 30 customer, PO received, deadline Friday'
                : 'e.g. shipped 3 weeks ago, invoice 30 days overdue, no reply'
            }
            data-testid="intl-override-reason"
          />
          <p className="mt-1 text-xs text-muted-foreground">
            {'Goes on the request permanently. In six months this is the only record of why '}
            {'this was the right call.'}
          </p>
        </div>

        {error ? (
          <p className="text-sm text-destructive" data-testid="intl-override-error">
            {error.message}
          </p>
        ) : null}

        <DialogFooter>
          <Button variant="outline" onClick={onClose}>
            {'Cancel'}
          </Button>
          <Button
            variant="destructive"
            onClick={run}
            disabled={loading || !ok}
            data-testid="intl-override-confirm"
          >
            {loading
              ? 'Working…'
              : shipping
                ? 'Approve and ship now'
                : 'Deactivate the account'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
